<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, EmptyState } from '@drama/ui'

import { getToken, session } from '../api.js'
import ChatPage from './ChatPage.vue'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const frame = ref(null)
const chatPage = ref(null)
const ready = ref(false)
const chatOpen = ref(false)
const voice = ref(false)
const error = ref('')
const subtitle = ref('')
const speaking = ref(false)
const speechState = ref('idle')
const dictating = ref(false)
const agents = computed(() => session.agents.filter(agent => agent.status !== 'deleted'))
const selected = computed(() => agents.value.find(agent => agent.id === route.params.id) ?? null)
const islandUrl = new URL(import.meta.env.VITE_ISLAND_URL || 'https://island.aajob.ai/play/')
islandUrl.searchParams.set('aajob_origin', window.location.origin)
const islandOrigin = islandUrl.origin
let utterance = null

watch(chatOpen, open => {
  if (open) chatPage.value.scrollToBottom()
}, { flush: 'post' })

if (getToken() === null) router.push({ path: '/login', query: { next: route.fullPath } })
watch(agents, list => {
  if (route.params.id === undefined && list.length > 0) router.replace(`/agent-island/${list[0].id}`)
}, { immediate: true })
watch(() => selected.value === null ? null : selected.value.id, id => {
  ready.value = false
  chatOpen.value = false
  subtitle.value = ''
  stopSpeech()
  if (id !== null) configure()
})

function post(message) {
  if (frame.value !== null) frame.value.contentWindow.postMessage(message, islandOrigin)
}
function configure() {
  if (selected.value !== null) post({ type: 'island.configure', agent: { id: selected.value.id, full_name: selected.value.full_name } })
}

/** Validate the cross-origin iframe boundary once, before using its event. */
function receive(event) {
  if (event.origin !== islandOrigin || frame.value === null || event.source !== frame.value.contentWindow) return
  const data = event.data
  if (data === null || typeof data !== 'object' || typeof data.type !== 'string') {
    error.value = t('agentIsland.protocolError')
    return
  }
  switch (data.type) {
    case 'island.ready':
      configure()
      break
    case 'island.configured':
    case 'island.agent-selected':
    case 'island.chat-closed':
      if (typeof data.agent_id !== 'string') {
        error.value = t('agentIsland.protocolError')
        return
      }
      if (selected.value === null || data.agent_id !== selected.value.id) return
      if (data.type === 'island.configured') ready.value = true
      else if (data.type === 'island.chat-closed') closeChat()
      else chatOpen.value = true
      break
    default:
      error.value = t('agentIsland.protocolError')
  }
}
onMounted(() => {
  window.addEventListener('message', receive)
  window.addEventListener('keydown', closeKey)
})
onBeforeUnmount(() => {
  window.removeEventListener('message', receive)
  window.removeEventListener('keydown', closeKey)
  stopSpeech()
})

function stopSpeech() {
  if ('speechSynthesis' in window) window.speechSynthesis.cancel()
  utterance = null
  speaking.value = false
}
function say(text) {
  stopSpeech()
  utterance = new window.SpeechSynthesisUtterance(text)
  utterance.lang = locale.value === 'zh' ? 'zh-CN' : locale.value
  speechState.value = 'requested'
  utterance.onstart = () => {
    speaking.value = true
    speechState.value = 'started'
  }
  utterance.onend = () => {
    speaking.value = false
    if (speechState.value !== 'started') {
      speechState.value = 'unavailable'
      error.value = t('agentIsland.voiceUnavailable')
      return
    }
    speechState.value = 'completed'
  }
  utterance.onerror = event => {
    speechState.value = event.error
    speaking.value = false
    if (event.error !== 'canceled' && event.error !== 'interrupted') error.value = t('agentIsland.speechError', { code: event.error })
  }
  window.speechSynthesis.speak(utterance)
}
function toggleVoice() {
  error.value = ''
  if (!('speechSynthesis' in window) || !('SpeechSynthesisUtterance' in window)) {
    error.value = t('agentIsland.voiceUnavailable')
    return
  }
  voice.value = !voice.value
  if (voice.value) say(t('agentIsland.voiceReady'))
  else stopSpeech()
}
function reply(message) {
  if (selected.value === null || message.agent_id !== selected.value.id) return
  subtitle.value = message.text
  if (voice.value && !dictating.value) say(message.text)
}
function closeKey(event) {
  if (!chatOpen.value || event.repeat || event.isComposing || event.ctrlKey || event.altKey || event.metaKey) return
  const editing = event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable]') !== null
  if (event.key !== 'Escape' && (event.code !== 'KeyE' || editing)) return
  event.preventDefault()
  closeChat()
}
function onDictation(active) {
  dictating.value = active
  if (active) stopSpeech()
}
function closeChat() {
  if (chatPage.value !== null) chatPage.value.abortDictation()
  stopSpeech()
  chatOpen.value = false
  post({ type: 'island.resume' })
}
function changeAgent(event) {
  closeChat()
  router.replace(`/agent-island/${event.target.value}`)
}
</script>

<template>
  <section class="island-page">
    <header class="island-toolbar">
      <h1>{{ t('agentIsland.title') }}</h1>
      <label>{{ t('agentIsland.agent') }}
        <select :value="route.params.id" :aria-label="t('agentIsland.agent')" @change="changeAgent">
          <option v-for="agent in agents" :key="agent.id" :value="agent.id">{{ agent.full_name }}</option>
        </select>
      </label>
      <Button variant="ghost" :aria-pressed="voice" :data-speech-state="speechState" @click="toggleVoice">{{ t(voice ? 'agentIsland.voiceOn' : 'agentIsland.voiceOff') }}</Button>
      <span v-if="selected !== null" class="voko-muted">{{ t(ready ? 'agentIsland.connected' : 'agentIsland.loading', { name: selected.full_name }) }}</span>
    </header>
    <p class="voko-muted island-instructions">{{ t('agentIsland.instructions') }}</p>
    <p v-if="error !== ''" role="alert" class="island-error">{{ error }}</p>
    <EmptyState v-if="selected === null" :title="t('chat.pick')" />
    <div v-else class="island-stage">
      <iframe ref="frame" :src="islandUrl.href" :title="t('agentIsland.title')" allow="autoplay; fullscreen" @load="configure" />
      <!-- ponytail: the open chat already displays the reply; show a separate subtitle only while exploring. -->
      <div v-if="subtitle !== '' && !chatOpen" class="island-subtitle" role="status" :data-speaking="speaking">
        <strong>{{ selected.full_name }}</strong>
        <p>{{ subtitle }}</p>
      </div>
      <section v-show="chatOpen" class="island-dialogue" :aria-label="t('chat.with', { name: selected.full_name })">
        <ChatPage ref="chatPage" embedded @reply="reply" @dictation="onDictation">
          <template #toolbar><Button size="sm" variant="ghost" :aria-label="t('agentIsland.closeHint')" @click="closeChat">{{ t('agentIsland.back') }} · E / Esc</Button></template>
        </ChatPage>
      </section>
    </div>
  </section>
</template>

<style scoped>
.island-page { display: flex; flex-direction: column; gap: var(--drama-space-sm); }
.island-toolbar { display: flex; align-items: center; flex-wrap: wrap; gap: var(--drama-space-md); }
.island-toolbar h1 { margin: 0; font-size: var(--drama-font-size-xl); }
.island-toolbar label { display: flex; gap: var(--drama-space-sm); align-items: center; }
.island-toolbar select { padding: var(--drama-space-sm); color: var(--drama-color-text); background: var(--drama-color-surface); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); }
.island-instructions { margin: 0; font-size: var(--drama-font-size-sm); }
.island-error { color: var(--drama-color-danger); }
.island-stage { position: relative; height: calc(100dvh - 180px); min-height: 480px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); overflow: hidden; background: var(--drama-color-surface-alt); }
.island-stage iframe { border: 0; width: 100%; height: 100%; display: block; }
.island-subtitle { position: absolute; inset: auto var(--drama-space-lg) var(--drama-space-lg); padding: var(--drama-space-md) var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); color: var(--drama-color-text); background: var(--drama-color-surface); pointer-events: none; max-height: 24%; overflow: auto; }
.island-subtitle p { white-space: pre-wrap; margin: var(--drama-space-xs) 0 0; line-height: 1.6; }
.island-dialogue { position: absolute; bottom: var(--drama-space-lg); left: 50%; transform: translateX(-50%); width: min(560px, calc(100% - 2 * var(--drama-space-lg))); background: var(--drama-color-surface); border-radius: var(--drama-radius-lg); box-shadow: var(--drama-shadow-lg); }
@media (max-width: 768px) { .island-stage { height: calc(100dvh - 220px); } .island-subtitle { left: var(--drama-space-sm); right: var(--drama-space-sm); } .island-dialogue { width: calc(100% - 2 * var(--drama-space-sm)); bottom: var(--drama-space-sm); } }
</style>
